import React from 'react';
import { Flex } from 'lib/components/styles/layout';
import { ParagraphSmall500 } from 'lib/components/styles/typography';
import { Option } from './types';
import { useTheme } from 'styled-components/macro';
import { DropdownForFolderShare } from 'lib/components/dropdown/DropdownForFolderShare';
import { AutomotiveRoleOptions, OemOptions } from 'lib/api/folderAccess/types';
import { Tooltip } from 'react-covideo-common';
import { MdInfo } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { getSecondaryColor } from 'lib/context/theme/utils';

type Props = {
  regionDropdownOptions?: Option[];
  storeDropdownOptions?: Option[];
  setSelectedRegions: (value: AutomotiveRoleOptions[]) => void;
  setSelectedStores: (value: AutomotiveRoleOptions[]) => void;
  selectedRegions?: AutomotiveRoleOptions[];
  selectedStores?: AutomotiveRoleOptions[];
  selectedOem?: OemOptions;
  selectedAutomotiveRoles: AutomotiveRoleOptions[];
  isRegionsDisabled?: boolean;
  isStoresDisabled?: boolean;
};

export const RegionsAndStores = ({
  regionDropdownOptions = [],
  storeDropdownOptions = [],
  setSelectedRegions,
  setSelectedStores,
  selectedRegions,
  selectedStores,
  selectedOem,
  selectedAutomotiveRoles,
  isRegionsDisabled,
  isStoresDisabled,
}: Props) => {
  const themes = useTheme();
  const { t } = useTranslation();
  const popupStyles = {
    padding: '10px',
    width: '300px',
    minWidth: '300px',
    maxWidth: '300px',
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '24px',
  };

  const formatLabel = (
    data: any,
    formatOptionMeta: { context: 'menu' | 'value' }
  ) => {
    const { context } = formatOptionMeta;
    if (context === 'menu') {
      return (
        <Flex gap='8px' padding='12px 8px' flexDirection='row'>
          <ParagraphSmall500 width='160px'>{data.label}</ParagraphSmall500>
        </Flex>
      );
    }
    return data.label;
  };

  return (
    <Flex justifyContent='center' alignItems='flex-start' gap='10px'>
      <Flex flexDirection='row' width='100%'>
        <ParagraphSmall500 color={themes.colors.neutral[80]}>
          {t('Select Regions or Stores*')}
        </ParagraphSmall500>
        <Tooltip
          position='bottom'
          extendStyles={{
            popup: popupStyles,
          }}
          popup={t(
            'Please select either a Region or a Store. To switch from one to the other, you must first remove all existing access permissions.'
          )}
        >
          <MdInfo size={24} color={getSecondaryColor(themes, 40)} />
        </Tooltip>
      </Flex>
      <Flex
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        width='100%'
        gap='8px'
      >
        <DropdownForFolderShare
          placeholder={t('Regions')}
          value={selectedRegions}
          onChange={setSelectedRegions}
          options={regionDropdownOptions}
          formatOptionLabel={formatLabel}
          disabled={
            !selectedAutomotiveRoles?.length ||
            selectedOem?.value === '0' ||
            !!selectedStores?.length ||
            isRegionsDisabled
          }
        />

        <DropdownForFolderShare
          placeholder={t('Stores')}
          value={selectedStores}
          onChange={setSelectedStores}
          options={storeDropdownOptions}
          formatOptionLabel={formatLabel}
          disabled={
            !selectedAutomotiveRoles?.length ||
            selectedOem?.value === '0' ||
            !!selectedRegions?.length ||
            isStoresDisabled
          }
        />
      </Flex>
    </Flex>
  );
};
